// packages
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'

// components
import { DropZone } from '@/components/uploaders/drop-zone'
import { FileList } from '@/components/uploaders/file-list'
import { UploadProgress } from '@/components/uploaders/upload-progress'

// hooks
import { useToast } from '@/hooks/use-toast'

// lib
import { showConfirm } from '@/lib/alerts'

// types
type FileProps = {
  file?: File
  name?: string
  base64?: string
  extension?: string
  url?: string
}
type FileUploadProps = {
  onUpload(files: File[]): void
  onClear(): void
  addedFiles?: File[] | FileProps[] | null
  maxFiles?: number
  multiple?: boolean
  accept?: {
    'image/*'?: string[]
    'application/*'?: string[]
    'audio/*'?: string[]
    'video/*'?: string[]
  }
  disabled?: boolean
  hideOnUpload?: boolean
}

const FileUpload = forwardRef(({ onUpload, onClear, addedFiles, maxFiles, multiple, accept, hideOnUpload, disabled }: FileUploadProps, ref) => {
  const [files, setFiles] = useState<File[] | FileProps[] | null>(addedFiles || [])
  const [uploading, setUploading] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [uploaded, setUploaded] = useState<boolean>(false)
  const { toast } = useToast()

  useEffect(() => {
    onUpload(files)
  }, [files])

  const handleDrop = (acceptedFiles: File[]) => {
    if (maxFiles && files.length + acceptedFiles.length > maxFiles) {
      toast({
        variant: 'destructive',
        title: 'Ops ...',
        description: 'Quantidade máxima de arquivos atingida.'
      })
    } else {
      setFiles(prev => [...prev, ...acceptedFiles])
      onUpload(files)
    }
  }

  const handleRemove = async (index: number) => {
    const { isConfirmed } = await showConfirm('Deseja confirmar?')
    if (!isConfirmed) return

    if (files?.length === 1 && onClear) onClear()

    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const _handleClear = () => {
    setFiles([])
    setUploading(false)
    setProgress(0)
    setUploaded(false)
  }

  useImperativeHandle(ref, () => ({
    clear: _handleClear
  }))

  return (
    <div className="w-full min-w-[80%] space-y-6">
      {(hideOnUpload ? !files?.length : true) && <DropZone onDrop={handleDrop} disabled={uploading || disabled} multiple={multiple} maxFiles={maxFiles} accept={accept} />}

      {files?.length > 0 && (
        <div className="space-y-4">
          <FileList files={files} uploaded={uploaded} onRemove={handleRemove} />

          {uploading && <UploadProgress progress={progress} />}
        </div>
      )}
    </div>
  )
})

FileUpload.displayName = 'FileUpload'

export { FileUpload }
